import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "components/ui/accordion";
import React, { FC, useRef, useMemo, useEffect, useState } from "react";
import { LineupGroup } from "../LineupGroup/LineupGroup";
import styles from "./ChannelSelector.module.scss";
import { SkeletonCheckboxLoader } from "components/ui/SkeletonCheckboxLoader";
import { TvPackage, TvAddon } from "core/API/GameMasterData";

// Define types for the data structures
interface LineupDetail {
  type: string;
  device: string;
  lineupId: string;
  name: string;
  location: string;
  mso: { id: string; name: string };
}

interface Channel {
  id?: number;
  lineup?: string;
  channel: string;
  call_sign: string;
  station_id: string;
}

interface CustomizedLineup {
  lineup: string;
  channels: Channel[];
}

interface ChannelSelectorProps {
  onChange: () => void;
  channel: Channel[];
  channeldata: Record<string, string[]>;
  lineups: LineupDetail[];
  values: { channels: Record<string, string[]> };
  change: (name: string, value: any) => void;
  tvPackages?: TvPackage[];
  selectedTvPackage?: number | null;
  onTvPackageChange?: (packageId: number) => void;
  tvAddons?: TvAddon[];
  selectedTvAddons?: number[];
  onTvAddonChange?: (addonIds: number[]) => void;
  isLoadingTvPackages?: boolean;
  readOnly?: boolean;
}

export const ChannelSelector: FC<any> = ({ 
  onChange, 
  channel, 
  channeldata, 
  lineups, 
  values, 
  change,
  tvPackages = [],
  selectedTvPackage = null,
  onTvPackageChange,
  tvAddons = [],
  selectedTvAddons = [],
  onTvAddonChange,
  isLoadingTvPackages = false,
  readOnly = false
}) => {
  // Local user-selected addons (non-forced)
  const [localSelectedAddons, setLocalSelectedAddons] = useState<number[]>(selectedTvAddons);
  useEffect(() => {
    setLocalSelectedAddons(selectedTvAddons);
  }, [selectedTvAddons]);
  const triggerRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [channelArray, setChannelArray] = useState<string[]>([]);

  // Initialize selected channels from form values or channeldata
  const [selectedChannelsByLineup, setSelectedChannelsByLineup] = useState<Record<string, string[]>>(
    lineups.reduce((acc, lineup) => {
      acc[lineup.lineupId] = values.channels[lineup.lineupId] || (channeldata[lineup.lineupId] || []).filter(
        (channel) => channel && channel.trim() !== ""
      );
      return acc;
    }, {} as Record<string, string[]>)
  );

  // Update selectedChannelsByLineup when values.channels or channeldata changes
  // But preserve existing selections to avoid overwriting manual changes
  useEffect(() => {
    setSelectedChannelsByLineup((prev) => {
      const updated = { ...prev };
      let hasChanges = false;
      
      lineups.forEach((lineup) => {
        // Prefer values.channels (most up-to-date), then channeldata, then keep existing
        // For DirectTV lineups, always prioritize values.channels to prevent count from hiding
        const isDirectTv = lineup.name?.startsWith("DIRECT");
        // For DIRECTV, preserve placeholders (_count_*) to maintain filtered count
        // For others, filter out empty channels
        const rawChannels = values.channels[lineup.lineupId] || channeldata[lineup.lineupId] || [];
        const newChannels = isDirectTv && selectedTvPackage !== null
          ? rawChannels // Preserve all channels including placeholders for DIRECTV
          : rawChannels.filter((channel) => channel && String(channel).trim() !== "");
        
        const currentChannels = prev[lineup.lineupId] || [];
        const currentSorted = currentChannels.sort().join(',');
        const newSorted = newChannels.sort().join(',');
        
        // For DirectTV: Update when package is selected and channels are filtered
        // For others: Update if channels are different
        if (isDirectTv) {
          // For DirectTV, if Custom package (7) is selected, allow manual selection
          if (selectedTvPackage === 7) {
            // Custom mode - allow user to manually select channels
            // Use values.channels (user's manual selections) or selectedChannelsByLineup
            // If both are empty, clear channels (initial state when Custom is selected)
            const valuesChannels = values.channels[lineup.lineupId] || [];
            const hasValuesChannels = valuesChannels.length > 0;
            const hasCurrentChannels = currentChannels.length > 0;
            
            if (hasValuesChannels) {
              // User has manually selected channels - update them
              if (currentSorted !== newSorted) {
                updated[lineup.lineupId] = newChannels;
                hasChanges = true;
              }
            } else if (!hasValuesChannels && hasCurrentChannels) {
              // values.channels is empty but we have current channels
              // This happens when Custom is first selected - clear them
              updated[lineup.lineupId] = [];
              hasChanges = true;
            }
          } else if (selectedTvPackage !== null) {
            // Package is selected (not Custom) - always update from values.channels when it has data
            if (values.channels[lineup.lineupId] && values.channels[lineup.lineupId].length > 0) {
              // New channels available - update immediately
              if (currentSorted !== newSorted) {
                updated[lineup.lineupId] = newChannels;
                hasChanges = true;
              }
            } else if (currentChannels.length > 0 && newChannels.length === 0) {
              // values.channels is empty but we have existing channels - preserve them temporarily
              // This prevents count from flickering during filtering
              // Keep current channels
            } else if (currentSorted !== newSorted) {
              // Update if different
              updated[lineup.lineupId] = newChannels;
              hasChanges = true;
            }
          } else {
            // No package selected - clear channels to hide count
            if (currentChannels.length > 0) {
              updated[lineup.lineupId] = [];
              hasChanges = true;
            }
          }
        } else {
          // For non-DirectTV lineups, update if channels are different
          if (currentSorted !== newSorted) {
            updated[lineup.lineupId] = newChannels;
            hasChanges = true;
          } else if (!prev[lineup.lineupId] && newChannels.length === 0) {
            // Initialize with empty array if not exists
            updated[lineup.lineupId] = [];
            hasChanges = true;
          }
        }
      });
      
      return hasChanges ? updated : prev;
    });
  }, [values.channels, channeldata, lineups, selectedTvPackage]);

  // Compute selected channels for each lineup (used for display and count)
  // Priority: selectedChannelsByLineup > values.channels > channeldata
  // Use selectedChannelsByLineup as primary source to prevent count from hiding
  const dat = useMemo(() => {
    return lineups.reduce((acc, lineup) => {
      // For DirectTV lineups, prefer selectedChannelsByLineup (which preserves filtered channels)
      // Then values.channels, then channeldata
      // This ensures count doesn't hide when values.channels temporarily becomes empty
      const isDirectTv = lineup.name?.startsWith("DIRECT");
      let channelsForLineup: string[] = [];
      
      if (isDirectTv) {
        // For DirectTV, check if Custom package is selected
        // In Custom mode, use manual selection from values.channels or selectedChannelsByLineup
        // Otherwise, use filtered channels from selectedChannelsByLineup
        if (selectedTvPackage === 7) {
          // Custom mode - use manual selection ONLY (user selects channels manually)
          // Don't use channeldata as fallback - only show manually selected channels
          channelsForLineup = selectedChannelsByLineup[lineup.lineupId] || 
                             values.channels[lineup.lineupId] || 
                             [];
        } else if (selectedTvPackage !== null) {
          // Package selected (non-Custom) - prioritize selectedChannelsByLineup (filtered channels)
          // This ensures count shows immediately when radio button is clicked
          channelsForLineup = selectedChannelsByLineup[lineup.lineupId] || 
                             values.channels[lineup.lineupId] || 
                             (channeldata[lineup.lineupId] || []);
        } else {
          // No package selected - use manual selection only
          channelsForLineup = values.channels[lineup.lineupId] || (channeldata[lineup.lineupId] || []);
        }
      } else {
        // For other lineups, use values.channels or fallback to channeldata
        channelsForLineup = values.channels[lineup.lineupId] || (channeldata[lineup.lineupId] || []);
      }
      
      acc[lineup.lineupId] = channelsForLineup.filter(
        (channel) => channel && String(channel).trim() !== ""
      );
      return acc;
    }, {} as Record<string, string[]>);
  }, [selectedChannelsByLineup, values.channels, channeldata, lineups, selectedTvPackage]);

  const handleAccordionOpen = (lineupId: string) => {
    const triggerButton = triggerRefs.current[lineupId];
    if (triggerButton) {
      triggerButton.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const customizedLineups = useMemo(() => {
    return lineups.map((lineup) => {
      const selectedChannels = dat[lineup.lineupId] || [];
      let lineupPackage = "";
      let lineupAddon = "";
      let channelsForLineup: {
        channel: string;
        call_sign: string;
        station_id: string;
      }[] = [];

      if (lineup.name?.startsWith("DIRECT")) {
        // DIRECTV lineup - include package/addon info
        if (selectedTvPackage === 7) {
          // Custom mode
          lineupPackage = "7";
          channelsForLineup =
            selectedChannels.length > 0
              ? selectedChannels.map((channel) => ({
                  channel,
                  call_sign: "",
                  station_id: "",
                }))
              : [{ channel: "", call_sign: "", station_id: "" }];
        } else if (selectedTvPackage) {
          // Predefined package
          lineupPackage = selectedTvPackage.toString();
          channelsForLineup =
            selectedChannels.length > 0
              ? selectedChannels.map((channel) => ({
                  channel,
                  call_sign: "",
                  station_id: "",
                }))
              : [{ channel: "", call_sign: "", station_id: "" }];
        }

        // Add addon IDs if any
        if (selectedTvAddons.length > 0) {
          lineupAddon = selectedTvAddons.join(",");
        }
      } else {
        // Non-DIRECTV lineup - no package/addon
        channelsForLineup =
          selectedChannels.length > 0
            ? selectedChannels.map((channel) => ({
                channel,
                call_sign: "",
                station_id: "",
              }))
            : [{ channel: "", call_sign: "", station_id: "" }];
      }

      return {
        lineup: lineup.lineupId,
        lineup_package: lineupPackage,
        lineup_addon: lineupAddon,
        channels: channelsForLineup,
      };
    });
  }, [lineups, selectedChannelsByLineup, selectedTvPackage, selectedTvAddons, dat]);

  useEffect(() => {
    if (customizedLineups.length > 0) {
      localStorage.setItem("customizedLineups", JSON.stringify(customizedLineups));
    }
  }, [customizedLineups]);

  // useEffect(() => {
  //   const storedLineups = localStorage.getItem("customizedLineups");
  //   if (storedLineups) {
  //     console.log("Loaded from localStorage:", JSON.parse(storedLineups))
  //   }
  // }, []);

  const [providerChannel, setProviderChannel] = useState<any[] | null>(null);
  const [openAccordions, setOpenAccordions] = useState<string[]>([]);
  
  // Keep all accordions open by default when lineups change
  useEffect(() => {
    if (lineups.length > 0) {
      const allLineupIds = lineups.map((lineup) => lineup.lineupId);
      setOpenAccordions(allLineupIds);
    }
  }, [lineups]);

  // Check if any selected lineup is DirectTV (name starts with "DIRECT")
  const hasDirectTvLineup = lineups.some(
    (lineup) => lineup.name && lineup.name.startsWith("DIRECT")
  );

  const handleTvPackageChange = (packageId: number) => {
    if (onTvPackageChange) {
      onTvPackageChange(packageId);
    }
  };

  // Helper function to check if addon belongs to selected package
  const getAddonsForPackage = (pkgId: number | null): number[] => {
    if (!pkgId || tvPackages.length === 0) return [];
    
    const pkg = tvPackages.find(p => p.id === pkgId);
    if (!pkg) return [];

    const addonIds: number[] = [];
    
    // Check addons array in package
    if (pkg.addons && Array.isArray(pkg.addons)) {
      pkg.addons.forEach((addon: any) => {
        if (typeof addon === 'string') {
          const match = addon.match(/\/(\d+)$/);
          if (match) addonIds.push(Number(match[1]));
        } else if (addon && typeof addon.id === 'number') {
          addonIds.push(addon.id);
        }
      });
    }

    // Also check addons that have this package in their tvPackages array
    tvAddons.forEach((addon) => {
      if (addon.tvPackages && Array.isArray(addon.tvPackages)) {
        const hasPackage = addon.tvPackages.some((pkgRef: any) => {
          if (typeof pkgRef === 'string') {
            return pkgRef.includes(`/tv_packages/${pkg.id}`);
          } else if (pkgRef && typeof pkgRef.id === 'number') {
            return pkgRef.id === pkg.id;
          }
          return false;
        });
        if (hasPackage && !addonIds.includes(addon.id)) {
          addonIds.push(addon.id);
        }
      }
    });

    return addonIds;
  };

  const handleAddonToggle = (addonId: number, isDisabled: boolean) => {
    if (isDisabled) return;
    
    const updated = localSelectedAddons.includes(addonId)
      ? localSelectedAddons.filter((id) => id !== addonId)
      : [...localSelectedAddons, addonId];
    
    setLocalSelectedAddons(updated);
    
    // Update parent component's addon selection
    // Parent's useEffect will handle filtering channels based on updated addons
    if (onTvAddonChange) {
      onTvAddonChange(updated);
    }
    
    // Don't manually filter here - let parent's useEffect handle it
    // This ensures proper grouping by lineupId for all DIRECTV lineups
    onChange();
  };

  return (
    <div className={styles.container}>
      {/* TV Packages - Radio buttons - Show above Channel Selection when DirectTV lineups are selected */}
      {hasDirectTvLineup && (
        <>
          {isLoadingTvPackages ? (
            <div className={styles.loadingText}>Loading TV packages...</div>
          ) : tvPackages.length > 0 ? (
            <>
              <div className={styles.tvPackagesGrid}>
                {tvPackages.map((pkg) => (
                  <div key={pkg.id} className={styles.tvPackageItem}>
                    <input
                      type="radio"
                      id={`tv-package-${pkg.id}`}
                      name="tv-package"
                      checked={selectedTvPackage === pkg.id}
                      onChange={() => handleTvPackageChange(pkg.id)}
                      disabled={readOnly}
                      className={styles.tvPackageRadio}
                    />
                    <label
                      htmlFor={`tv-package-${pkg.id}`}
                      className={styles.tvPackageLabel}
                    >
                      {pkg.name}
                    </label>
                  </div>
                ))}
              </div>

              {/* Stand-Alone Packages (TV Addons) - Hide when no package selected or Custom package is selected */}
              {tvAddons.length > 0 && selectedTvPackage !== null && selectedTvPackage !== 7 && (
                <>
                  <div className={styles.separator}></div>
                   <h2 className={styles.heading}>Stand-Alone Packages</h2>
                  <div className={styles.tvAddonsGrid}>
                    {tvAddons.map((addon) => {
                      const forcedIds = getAddonsForPackage(selectedTvPackage);
                      const isDisabled = forcedIds.includes(addon.id); // disable only when part of selected package
                      const isChecked = localSelectedAddons.includes(addon.id) || isDisabled; // always check forced
                      return (
                        <div key={addon.id} className={styles.tvAddonItem}>
                          <input
                            type="checkbox"
                            id={`tv-addon-${addon.id}`}
                            checked={isChecked}
                            disabled={isDisabled || readOnly}
                            onChange={() => handleAddonToggle(addon.id, isDisabled)}
                            className={styles.tvAddonCheckbox}
                          />
                          <label
                            htmlFor={`tv-addon-${addon.id}`}
                            className={`${styles.tvAddonLabel} ${isDisabled ? styles.disabled : ''}`}
                          >
                            {addon.name}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          ) : null}
        </>
      )}

      <h2 className={styles.heading}>Channel Selection</h2>
      <p className={styles.description}>
        Select channels from the available providers. Channels are grouped by provider.
      </p>

      <Accordion
        type="multiple"
        className={styles.accordion}
        value={openAccordions}
        onValueChange={(lineupIds) => {
          // Always keep all accordions open - prevent closing
          const allLineupIds = lineups.map((lineup) => lineup.lineupId);
          setOpenAccordions(allLineupIds);
          
          // Handle scroll if needed
          if (lineupIds && lineupIds.length > 0) {
            const lastOpened = lineupIds[lineupIds.length - 1];
            handleAccordionOpen(lastOpened);
          }
        }}
      >
        {lineups?.length === 0 ? (
          <p className={styles.noProvider}>No provider selected.</p>
        ) : (
          lineups?.map((lineupItem: LineupDetail, i: number) => {
            // Get channels for count calculation
            const isDirectTv = lineupItem.name?.startsWith("DIRECT");
            
            // For DIRECTV with package selected, use the source that has channels
            // For Custom mode, use manual selection from selectedChannelsByLineup or values.channels ONLY
            // For others, use selectedChannelsByLineup or dat
            let selectedChannels: string[] = [];
            if (isDirectTv && selectedTvPackage !== null) {
              // For Custom mode (packageId === 7), use manual selection ONLY
              if (selectedTvPackage === 7) {
                // Custom mode - prioritize selectedChannelsByLineup (user's manual selections)
                // Don't use channeldata or dat as fallback - only show manually selected channels
                const selectedChannelsByLineupData = selectedChannelsByLineup[lineupItem.lineupId] || [];
                const valuesChannels = values.channels[lineupItem.lineupId] || [];
                selectedChannels = selectedChannelsByLineupData.length > 0 
                  ? selectedChannelsByLineupData 
                  : valuesChannels;
              } else {
                // DIRECTV with package (not Custom) - prioritize channeldata (filtered channels) for count display
                // channeldata contains filtered channels based on package/addon, which is needed for accurate count
                const channeldataChannels = channeldata[lineupItem.lineupId] || [];
                const valuesChannels = values.channels[lineupItem.lineupId] || [];
                const selectedChannelsByLineupData = selectedChannelsByLineup[lineupItem.lineupId] || [];
                
                // For count display, prioritize channeldata (filtered channels) when available
                // This ensures count shows correctly based on package/addon selection
                selectedChannels = channeldataChannels.length > 0 
                  ? channeldataChannels 
                  : (valuesChannels.length > 0 
                    ? valuesChannels 
                    : (selectedChannelsByLineupData.length > 0 
                      ? selectedChannelsByLineupData 
                      : (dat[lineupItem.lineupId] || [])));
              }
              
            } else {
              // Non-DIRECTV or no package - use standard sources
              selectedChannels = selectedChannelsByLineup[lineupItem.lineupId] || dat[lineupItem.lineupId] || [];
            }
            
            // Count calculation
            // For DIRECTV with package selected (but not Custom), use total array length (includes placeholders for filtered count)
            // For Custom mode, count manually selected channels
            // For others, count only non-empty channels
            let finalCount: number;
            if (isDirectTv && selectedTvPackage !== null && selectedTvPackage !== 7) {
              // For DIRECTV (non-Custom), use total length to show filtered count (80) not grouped count (79)
              // Placeholders are added in filterChannelNumbers to match the filtered count
              finalCount = selectedChannels.length;
            } else {
              // For non-DIRECTV or Custom mode, count only non-empty channels
              finalCount = selectedChannels.filter((ch) => ch && String(ch).trim() !== "").length;
            }
            
            // For DIRECTV lineups, show count if:
            // 1. A radio button (package) is selected AND channels exist
            // 2. For Custom mode, show count when user manually selects channels
            let shouldShowCount: boolean;
            if (isDirectTv) {
              // Show count if package is selected (including Custom) and channels exist
              shouldShowCount = selectedTvPackage !== null && finalCount > 0;
            } else {
              // Non-DIRECTV lineups - show count if channels exist
              shouldShowCount = finalCount > 0;
            }

            return (
              <AccordionItem key={i} value={lineupItem.lineupId} className={styles.accordionItem}>
                <AccordionTrigger
                  className={styles.accordionTrigger}
                  ref={(el: HTMLButtonElement | null) => {
                    if (el) {
                      triggerRefs.current[lineupItem.lineupId] = el;
                    }
                  }}
                >
                  <h6 className={styles.triggerHeading}>
                    {lineupItem.name}{" "}
                    {shouldShowCount ? `(${finalCount})` : ""}
                  </h6>
                </AccordionTrigger>

                <AccordionContent className={styles.accordionContent}>
                  <LineupGroup
                    setChannelArray={setChannelArray}
                    providerChannel={providerChannel}
                    masterChannels={channelArray}
                    key={i}
                    lineup={lineupItem}
                    selectedChannels={selectedChannels}
                    selectedTvPackage={selectedTvPackage}
                    readOnly={readOnly}
                    onChannelsChange={(channels: string[]) => {
                      // Immediately update local state for instant UI feedback
                      setSelectedChannelsByLineup((prev) => ({
                        ...prev,
                        [lineupItem.lineupId]: channels,
                      }));
                      
                      // Update form state (this will trigger parent updates)
                      change("channels", {
                        ...values.channels,
                        [lineupItem.lineupId]: channels,
                      });
                      
                      // Update channelArray with all selected channels across lineups
                      const allChannels = Object.values({
                        ...selectedChannelsByLineup,
                        [lineupItem.lineupId]: channels,
                      }).flat();
                      setChannelArray(allChannels);
                      
                      // Trigger parent onChange
                      onChange();
                    }}
                  />
                </AccordionContent>
              </AccordionItem>

            );
          })
        )}
      </Accordion>
    </div>
  );
};